"use client";

import React, { useCallback, useRef, useState } from "react";
import { goUrl, type GoTab } from "@/lib/go";

export interface GoLinkProps extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  storeId: string;       // slug (para goUrl fallback)
  storeDbId?: string;    // ID real de BD (para /api/go)
  productId?: string;
  tab?: GoTab;
  url?: string;
  clickId?: number;      // ID pre-generado server-side → se usa directamente
  children: React.ReactNode;
}

/**
 * Enlace de salida a tienda con CLICKID progresivo (/go/12345).
 *
 * - Si llega `clickId` pre-generado (server-side): href ya es /go/ID, sin JS extra.
 * - Sin clickId: prefetch en hover/focus → actualiza href antes del clic.
 * - onClick sin prefetch (tap móvil): fetch al vuelo, window.open al ID.
 */
export function GoLink({
  storeId,
  storeDbId,
  productId,
  tab,
  url,
  clickId: propClickId,
  children,
  className,
  onClick,
  ...rest
}: GoLinkProps) {
  const [clickId, setClickId] = useState<number | null>(propClickId ?? null);
  const fetching = useRef(false);
  const fallback = goUrl({ s: storeId, u: url, p: productId, t: tab });

  const prefetch = useCallback(async () => {
    if (clickId !== null || fetching.current) return;
    fetching.current = true;
    try {
      const r = await fetch("/api/go", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ storeId: storeDbId ?? storeId, productId, section: tab, url }),
      });
      const { id } = (await r.json()) as { id?: number };
      if (typeof id === "number") setClickId(id);
    } catch {
      // fallback href sigue funcionando
    } finally {
      fetching.current = false;
    }
  }, [storeId, storeDbId, productId, tab, url, clickId]);

  const href = clickId !== null ? `/go/${clickId}` : fallback;

  function handleClick(e: React.MouseEvent<HTMLAnchorElement>) {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.button !== 0) {
      onClick?.(e);
      return;
    }

    onClick?.(e);

    if (clickId !== null) {
      // href ya es /go/ID — el browser lo abre en _blank, no hacemos nada más.
      return;
    }

    // Sin prefetch (tap móvil, clic rápido): creamos el ID al vuelo.
    e.preventDefault();
    fetch("/api/go", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ storeId: storeDbId ?? storeId, productId, section: tab, url }),
    })
      .then((r) => r.json())
      .then(({ id }: { id: number }) => {
        window.open(`/go/${id}`, "_blank");
      })
      .catch(() => {
        window.open(fallback, "_blank");
      });
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer nofollow sponsored"
      className={className}
      onMouseEnter={prefetch}
      onFocus={prefetch}
      onClick={handleClick}
      {...rest}
    >
      {children}
    </a>
  );
}
